import React, { useCallback, useEffect, useRef, useState } from 'react';
import getLiveSocket, { normalizeTile } from '../../liveSocket';
import './VerifiedTileNotifications.css';

// The same square can reach us more than once: the socket server sends a
// per-square `tileConfirmed` event AND a full `confirmedTiles` snapshot on
// connect, and a host can un-confirm and re-confirm a square while fixing a
// mistake. Ignore a repeat of the same square inside this window so a player
// only ever sees one toast per square.
const DEDUPE_MS = 60000;

const VerifiedTileNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(true);
  const lastShown = useRef(new Map());        // normalised square text -> timestamp

  // Single entry point for the feed, so de-duplication cannot be bypassed.
  // Returns false when this square was already announced moments ago.
  const pushNotification = useCallback((tileText) => {
    const key = normalizeTile(tileText);
    if (!key) return false;

    const now = Date.now();
    const seen = lastShown.current.get(key);
    if (seen && now - seen < DEDUPE_MS) return false;
    lastShown.current.set(key, now);

    // A long show must not grow this map without bound.
    if (lastShown.current.size > 200) {
      lastShown.current.forEach((t, k) => {
        if (now - t > DEDUPE_MS) lastShown.current.delete(k);
      });
    }

    const id = `${now}-${Math.random().toString(36).slice(2, 8)}`;
    setNotifications((prev) => [{ id, message: `${tileText} has been verified ✅`, fadeOut: false }, ...prev]);
    setTimeout(() => {
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, fadeOut: 'partial' } : n)));
    }, 5000);
    return true;
  }, []);

  // The admin panel's Live Show console writes the square to the database and
  // the socket server relays it here as `tileConfirmed`. This is the only feed.
  useEffect(() => {
    if (!showNotifications) return undefined;
    const socket = getLiveSocket();
    const onTileConfirmed = (data) => {
      if (data && data.tileText) pushNotification(data.tileText);
    };
    socket.on('tileConfirmed', onTileConfirmed);
    return () => {
      // Shared connection (see src/liveSocket.js): drop the listener, never the
      // socket itself -- the card is using it too.
      socket.off('tileConfirmed', onTileConfirmed);
    };
  }, [showNotifications, pushNotification]);

  const closeNotification = (id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);
  };

  return (
    <div className="notification-container">
      {showNotifications && notifications.map((notification) => (
        <div key={notification.id} className={`notification-message ${notification.fadeOut === 'partial' ? 'fade-to-partial' : ''}`}>
          {notification.message}
          <i className="fa fa-times close-icon" onClick={() => closeNotification(notification.id)}></i>
        </div>
      ))}
      <button className="toggle-notifications-button" onClick={toggleNotifications}>
        {showNotifications ? 'Hide All Notifications' : 'Show Notifications'}
      </button>
    </div>
  );
};

export default VerifiedTileNotifications;
